// PayrollPanel — HR & payroll: salary structure per staff member, monthly
// payroll generated from staff check-ins, approved leave, holidays and
// weekly offs, mark paid, and printable payslips.
import { useEffect, useState } from 'react';
import { Pencil, Calculator, Printer, CheckCircle2, Undo2, Trash2, Download, SlidersHorizontal } from 'lucide-react';
import { getSalaries, saveSalary, getPayslips, generatePayroll, payPayslip, unpayPayslip, deletePayslip, adjustPayslip } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader, StatTile } from '../ui/Card';
import { Field, Msg, Modal, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import PrintSheet, { PrintHeader } from '../ui/PrintSheet';
import { inr, fmtDate, fmtMonth, todayISO, downloadCsv, PAYMENT_MODES } from '../ui/format';

export default function PayrollPanel({ schoolName }) {
  const [tab, setTab] = useState('payroll');
  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">HR &amp; Payroll</h2>
      <div className="flex gap-1.5 mb-5 bg-transit-tint p-1 rounded-m w-fit">
        {[['payroll', 'Monthly payroll'], ['salaries', 'Salary setup']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-s text-[13px] font-bold ${tab === k ? 'bg-transit text-white shadow-1' : 'text-transit-deep'}`}>{label}</button>
        ))}
      </div>
      {tab === 'payroll' ? <MonthlyPayroll schoolName={schoolName} onSetup={() => setTab('salaries')} /> : <SalarySetup />}
    </div>
  );
}

// ── Salary setup ────────────────────────────────────────────
function SalarySetup() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [msg,  setMsg]  = useState('');
  useEffect(() => { load(); }, []);
  async function load() { try { setRows(await getSalaries()); } catch (err) { setMsg(errText(err)); } }

  async function save(e) {
    e.preventDefault(); setMsg('');
    try { await saveSalary(edit.staff_id, edit); setMsg(`Salary saved for ${edit.name} ✅`); setEdit(null); load(); }
    catch (err) { setMsg(errText(err)); }
  }
  const open = (s) => setEdit({
    ...s, basic: s.basic ?? '', hra: s.hra ?? 0, da: s.da ?? 0, other_allowance: s.other_allowance ?? 0,
    other_deduction: s.other_deduction ?? 0, pf_enabled: !!s.pf_enabled, date_of_joining: s.date_of_joining || '',
    bank_account: s.bank_account || '', ifsc: s.ifsc || '', pan: s.pan || '',
  });
  const gross = edit ? ['basic', 'hra', 'da', 'other_allowance'].reduce((t, k) => t + (Number(edit[k]) || 0), 0) : 0;

  return (
    <>
      <Msg msg={msg} className="mb-3" />
      <Card>
        <CardHeader title={`Staff (${rows.length}) · ${rows.filter(r => r.configured).length} with salary set`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Staff</th><th className="px-4 py-2.5 text-right">Basic</th><th className="px-4 py-2.5 text-right">Allowances</th><th className="px-4 py-2.5 text-right">Gross / month</th><th className="px-4 py-2.5">PF</th><th className="px-4 py-2.5">Bank</th><th className="px-4 py-2.5"></th></tr></thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.staff_id} className="border-t border-line-soft">
                  <td className="px-4 py-3 font-semibold">{s.name}<span className="block text-xs text-ink-soft font-normal">{s.designation}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.configured ? inr(s.basic) : '—'}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.configured ? inr(s.hra + s.da + s.other_allowance) : '—'}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold">{s.configured ? inr(s.gross) : <Badge tone="caution">Not set</Badge>}</td>
                  <td className="px-4 py-3">{s.configured ? (s.pf_enabled ? 'Yes' : 'No') : '—'}</td>
                  <td className="px-4 py-3 text-ink-soft">{s.bank_account ? `…${s.bank_account.slice(-4)}${s.ifsc ? ` · ${s.ifsc}` : ''}` : '—'}</td>
                  <td className="px-4 py-3 text-right"><Button variant="secondary" className="!px-2.5 !py-1 !text-xs inline-flex items-center gap-1" onClick={() => open(s)}><Pencil size={12} /> {s.configured ? 'Edit' : 'Set salary'}</Button></td>
                </tr>
              ))}
              {!rows.length && <EmptyRow cols={7}>Add staff on the Staff page first</EmptyRow>}
            </tbody>
          </table>
        </div>
      </Card>

      {edit && (
        <Modal title={`Salary — ${edit.name}`} onClose={() => setEdit(null)} width={560}>
          <form onSubmit={save} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Basic (₹/month)"><input required type="number" min="1" step="any" className={inputCls} value={edit.basic} onChange={e => setEdit({ ...edit, basic: e.target.value })} /></Field>
              <Field label="HRA"><input type="number" min="0" step="any" className={inputCls} value={edit.hra} onChange={e => setEdit({ ...edit, hra: e.target.value })} /></Field>
              <Field label="DA"><input type="number" min="0" step="any" className={inputCls} value={edit.da} onChange={e => setEdit({ ...edit, da: e.target.value })} /></Field>
              <Field label="Other allowance"><input type="number" min="0" step="any" className={inputCls} value={edit.other_allowance} onChange={e => setEdit({ ...edit, other_allowance: e.target.value })} /></Field>
              <Field label="Fixed deduction (e.g. advance, TDS)"><input type="number" min="0" step="any" className={inputCls} value={edit.other_deduction} onChange={e => setEdit({ ...edit, other_deduction: e.target.value })} /></Field>
              <Field label="Date of joining"><input type="date" className={inputCls} value={edit.date_of_joining} onChange={e => setEdit({ ...edit, date_of_joining: e.target.value })} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <input type="checkbox" checked={edit.pf_enabled} onChange={e => setEdit({ ...edit, pf_enabled: e.target.checked })} className="accent-[var(--transit)]" />
              Deduct PF (12% of basic, on basic up to ₹15,000)
            </label>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Bank account no."><input className={inputCls} value={edit.bank_account} onChange={e => setEdit({ ...edit, bank_account: e.target.value })} /></Field>
              <Field label="IFSC"><input className={inputCls} value={edit.ifsc} onChange={e => setEdit({ ...edit, ifsc: e.target.value })} /></Field>
              <Field label="PAN"><input className={inputCls} value={edit.pan} onChange={e => setEdit({ ...edit, pan: e.target.value })} /></Field>
            </div>
            <p className="text-sm text-ink">Gross: <b>{inr(gross)}</b> / month</p>
            <Button type="submit" className="w-fit">Save salary</Button>
          </form>
        </Modal>
      )}
    </>
  );
}

// ── Monthly payroll ─────────────────────────────────────────
function MonthlyPayroll({ schoolName, onSetup }) {
  const [month, setMonth] = useState(() => todayISO().slice(0, 7));
  const [data,  setData]  = useState(null);
  const [lop,   setLop]   = useState(false);
  const [busy,  setBusy]  = useState(false);
  const [msg,   setMsg]   = useState('');
  const [paying, setPaying] = useState(null);
  const [slip,  setSlip]  = useState(null);
  const [adjust, setAdjust] = useState(null); // { p, lop_days, note }

  useEffect(() => { load(); }, [month]);
  async function load() { try { setData(await getPayslips(month)); } catch (err) { setMsg(errText(err)); } }

  async function generate() {
    setBusy(true); setMsg('');
    try {
      const r = await generatePayroll({ month, apply_lop: lop });
      await load();
      setMsg(`Payroll ready for ${r.generated} staff ✅ · ${r.working_days} working days` +
        (r.skipped_paid ? ` · ${r.skipped_paid} already paid, left unchanged` : '') +
        (r.month_complete ? '' : ' · month not over yet — regenerate at month end'));
    } catch (err) { setMsg(errText(err)); }
    finally { setBusy(false); }
  }
  async function pay(e) {
    e.preventDefault();
    try { await payPayslip(paying.id, { mode: paying.mode, paid_on: paying.paid_on }); setPaying(null); setMsg(`Salary marked paid ✅`); load(); }
    catch (err) { setMsg(errText(err)); }
  }
  async function undo(p) {
    if (!window.confirm(`Mark ${p.name}'s salary as unpaid again?`)) return;
    try { await unpayPayslip(p.id); load(); } catch (err) { setMsg(errText(err)); }
  }
  async function remove(p) {
    if (!window.confirm(`Delete the draft payslip for ${p.name}?`)) return;
    try { await deletePayslip(p.id); load(); } catch (err) { setMsg(errText(err)); }
  }
  async function saveAdjust(e, overrideLop) {
    e?.preventDefault();
    const lop = overrideLop ?? Number(adjust.lop_days);
    try {
      const r = await adjustPayslip(adjust.p.id, { lop_days: lop, note: adjust.note });
      setMsg(lop === 0 ? `${r.name} will get full salary (${inr(r.net)}) ✅` : `${r.name}: ${lop} loss-of-pay day${lop === 1 ? '' : 's'} · net ${inr(r.net)} ✅`);
      setAdjust(null); load();
    } catch (err) { setMsg(errText(err)); }
  }
  async function resetAdjust() {
    try { await adjustPayslip(adjust.p.id, { reset: true }); setAdjust(null); await generate(); }
    catch (err) { setMsg(errText(err)); }
  }

  async function payAll() {
    const drafts = data.rows.filter(r => r.status === 'draft');
    if (!window.confirm(`Mark all ${drafts.length} unpaid salaries as paid by bank transfer today?`)) return;
    try { for (const p of drafts) await payPayslip(p.id, { mode: 'Bank transfer' }); setMsg(`${drafts.length} salaries marked paid ✅`); load(); }
    catch (err) { setMsg(errText(err)); }
  }

  const drafts = data ? data.rows.filter(r => r.status === 'draft').length : 0;

  return (
    <>
      <Card className="p-4 mb-4 flex flex-wrap items-end gap-3">
        <Field label="Month"><input type="month" className={inputCls} value={month} max={todayISO().slice(0, 7)} onChange={e => setMonth(e.target.value || todayISO().slice(0, 7))} /></Field>
        <label className="flex items-center gap-2 text-sm font-semibold text-ink max-w-[360px]">
          <input type="checkbox" checked={lop} onChange={e => setLop(e.target.checked)} className="accent-[var(--transit)]" />
          Deduct loss of pay for working days with no check-in and no approved leave
        </label>
        <div className="flex gap-2 ml-auto">
          <Button onClick={generate} disabled={busy} className="flex items-center gap-1.5"><Calculator size={14} /> {busy ? 'Calculating…' : data?.rows.length ? 'Recalculate' : 'Generate payroll'}</Button>
          {drafts > 0 && <Button variant="secondary" onClick={payAll}>Mark all paid</Button>}
        </div>
      </Card>
      <p className="text-xs text-ink-soft mb-3 -mt-2">Leave this unticked and everyone gets full salary. If you tick it, use <b>Adjust</b> on anyone who was present but forgot to check in. Paid payslips are never changed by recalculating.</p>
      <Msg msg={msg} className="mb-3" />

      {data && data.rows.length > 0 && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3.5 mb-4">
          <StatTile tone="transit" label="Gross" value={inr(data.totals.gross)} caption={`${data.totals.staff} staff`} />
          <StatTile tone="caution" label="Deductions" value={inr(data.totals.deductions)} />
          <StatTile tone="success" label="Net payable" value={inr(data.totals.net)} />
          <StatTile tone={data.totals.pending ? 'critical' : 'slate'} label="Still to pay" value={inr(data.totals.pending)} caption={`${inr(data.totals.paid)} paid`} />
        </div>
      )}

      <Card>
        <CardHeader title={`Payroll — ${fmtMonth(month)}`} action={data?.rows.length ? (
          <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" onClick={() => downloadCsv(`payroll-${month}.csv`, [
            { key: 'name', label: 'Staff' }, { key: 'designation', label: 'Designation' }, { key: 'working_days', label: 'Working days' },
            { key: 'present_days', label: 'Present' }, { key: 'leave_days', label: 'Leave' }, { key: 'lop_days', label: 'LOP days' },
            { key: 'gross', label: 'Gross' }, { key: 'pf', label: 'PF' }, { key: 'lop_amount', label: 'LOP' }, { key: 'other_deduction', label: 'Other deduction' },
            { key: 'net', label: 'Net' }, { key: 'status', label: 'Status' }, { key: 'paid_on', label: 'Paid on' }, { key: 'bank_account', label: 'Bank account' }, { key: 'ifsc', label: 'IFSC' },
          ], data.rows)}><Download size={13} /> CSV for bank</Button>
        ) : null} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Staff</th><th className="px-4 py-2.5">Days (present / leave / LOP)</th><th className="px-4 py-2.5 text-right">Gross</th><th className="px-4 py-2.5 text-right">Deductions</th><th className="px-4 py-2.5 text-right">Net</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th></tr></thead>
            <tbody>
              {data?.rows.map(p => (
                <tr key={p.id} className="border-t border-line-soft">
                  <td className="px-4 py-3 font-semibold">{p.name}<span className="block text-xs text-ink-soft font-normal">{p.designation}</span></td>
                  <td className="px-4 py-3 tabular-nums">{p.present_days} / {p.leave_days} / {p.lop_days}<span className="block text-xs text-ink-soft">of {p.working_days} working days</span>
                    {p.lop_override && <span className="block mt-0.5" title={p.adjust_note || ''}><Badge tone="info">Adjusted{p.adjust_note ? `: ${p.adjust_note}` : ''}</Badge></span>}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{inr(p.gross)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-critical">{inr(p.pf + p.lop_amount + p.other_deduction)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold">{inr(p.net)}</td>
                  <td className="px-4 py-3">{p.status === 'paid' ? <Badge tone="success">Paid {fmtDate(p.paid_on)}</Badge> : <Badge tone="caution">Unpaid</Badge>}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <button onClick={() => setSlip(p)} aria-label={`Payslip for ${p.name}`} className="text-transit-deep p-1 mr-1"><Printer size={14} /></button>
                    {p.status === 'draft' ? (
                      <>
                        <Button variant="secondary" className="!px-2.5 !py-1 !text-xs inline-flex items-center gap-1 mr-1"
                          onClick={() => setAdjust({ p, lop_days: p.lop_days, note: p.adjust_note || '' })}><SlidersHorizontal size={12} /> Adjust</Button>
                        <Button className="!px-2.5 !py-1 !text-xs inline-flex items-center gap-1" onClick={() => setPaying({ id: p.id, name: p.name, net: p.net, mode: 'Bank transfer', paid_on: todayISO() })}><CheckCircle2 size={12} /> Pay</Button>
                        <button onClick={() => remove(p)} aria-label="Delete draft" className="text-ink-soft hover:text-critical p-1 ml-1"><Trash2 size={13} /></button>
                      </>
                    ) : (
                      <button onClick={() => undo(p)} aria-label="Undo paid" className="text-ink-soft hover:text-ink p-1"><Undo2 size={13} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {data && !data.rows.length && (
                <EmptyRow cols={7}>No payroll for {fmtMonth(month)} yet — click Generate payroll. <button onClick={onSetup} className="text-transit-deep font-bold hover:underline">Set up salaries</button> first if you haven't.</EmptyRow>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {adjust && (
        <Modal title={`Adjust salary — ${adjust.p.name}`} onClose={() => setAdjust(null)} width={460}>
          <form onSubmit={saveAdjust} className="flex flex-col gap-3">
            <p className="text-sm text-ink-soft">
              {adjust.p.working_days} working days · checked in on <b className="text-ink">{adjust.p.present_days}</b> · approved leave <b className="text-ink">{adjust.p.leave_days}</b>.
              Use this when someone was at school but forgot to check in on the staff app.
            </p>
            <Button type="button" onClick={e => saveAdjust(e, 0)} className="flex items-center justify-center gap-1.5">
              <CheckCircle2 size={14} /> Give full salary ({inr(adjust.p.gross - adjust.p.pf - adjust.p.other_deduction)})
            </Button>
            <div className="text-xs font-bold text-ink-soft text-center">— or set the loss-of-pay days —</div>
            <div className="grid grid-cols-[120px_1fr] gap-3">
              <Field label="Loss-of-pay days"><input required type="number" min="0" max={adjust.p.working_days} step="1" className={inputCls} value={adjust.lop_days} onChange={e => setAdjust({ ...adjust, lop_days: e.target.value })} /></Field>
              <Field label="Reason"><input maxLength={200} className={inputCls} placeholder="e.g. Forgot to check in, was present" value={adjust.note} onChange={e => setAdjust({ ...adjust, note: e.target.value })} /></Field>
            </div>
            <p className="text-sm">Net pay will be <b>{inr(Math.max(0, adjust.p.gross - adjust.p.pf - adjust.p.other_deduction - (adjust.p.working_days ? (adjust.p.gross / adjust.p.working_days) * (Number(adjust.lop_days) || 0) : 0)))}</b></p>
            <div className="flex gap-2">
              <Button type="submit" variant="secondary">Save</Button>
              {adjust.p.lop_override && <Button type="button" variant="ghost" onClick={resetAdjust}>Back to automatic</Button>}
            </div>
            <p className="text-xs text-ink-soft">Recalculating payroll keeps this change.</p>
          </form>
        </Modal>
      )}

      {paying && (
        <Modal title={`Pay salary — ${paying.name}`} onClose={() => setPaying(null)} width={400}>
          <form onSubmit={pay} className="flex flex-col gap-3">
            <p className="text-sm">Net pay: <b className="text-lg">{inr(paying.net)}</b></p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Paid by"><select className={inputCls} value={paying.mode} onChange={e => setPaying({ ...paying, mode: e.target.value })}>{PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}</select></Field>
              <Field label="Paid on"><input type="date" className={inputCls} value={paying.paid_on} onChange={e => setPaying({ ...paying, paid_on: e.target.value })} /></Field>
            </div>
            <p className="text-xs text-ink-soft">This is recorded automatically as a salary expense in Income &amp; Expenses.</p>
            <Button type="submit" className="w-fit">Mark paid</Button>
          </form>
        </Modal>
      )}

      {slip && (
        <PrintSheet title={`Payslip — ${slip.name}`} onClose={() => setSlip(null)}>
          <PrintHeader schoolName={schoolName} docTitle={`Payslip · ${fmtMonth(`${slip.year}-${String(slip.month).padStart(2, '0')}`)}`}
            right={slip.status === 'paid' ? <>Paid {fmtDate(slip.paid_on)}<br />{slip.payment_mode}</> : <>Not yet paid</>} />
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[13.5px] mb-4">
            <span>Name: <b>{slip.name}</b></span><span>Designation: <b>{slip.designation}</b></span>
            <span>Date of joining: <b>{slip.date_of_joining ? fmtDate(slip.date_of_joining) : '—'}</b></span><span>PAN: <b>{slip.pan || '—'}</b></span>
            <span>Bank A/c: <b>{slip.bank_account || '—'}</b></span><span>IFSC: <b>{slip.ifsc || '—'}</b></span>
            <span>Working days: <b>{slip.working_days}</b></span><span>Present / leave / LOP: <b>{slip.present_days} / {slip.leave_days} / {slip.lop_days}</b></span>
          </div>
          <table className="w-full text-sm border-collapse">
            <thead><tr><th className="text-left">Earnings</th><th className="text-right">Amount</th><th className="text-left">Deductions</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              <tr><td>Basic</td><td className="text-right">{inr(slip.basic)}</td><td>Provident fund</td><td className="text-right">{inr(slip.pf)}</td></tr>
              <tr><td>Allowances (HRA, DA, other)</td><td className="text-right">{inr(slip.allowances)}</td><td>Loss of pay ({slip.lop_days} days)</td><td className="text-right">{inr(slip.lop_amount)}</td></tr>
              <tr><td></td><td></td><td>Other deductions</td><td className="text-right">{inr(slip.other_deduction)}</td></tr>
              <tr><td className="font-bold">Gross earnings</td><td className="text-right font-bold">{inr(slip.gross)}</td><td className="font-bold">Total deductions</td><td className="text-right font-bold">{inr(slip.pf + slip.lop_amount + slip.other_deduction)}</td></tr>
            </tbody>
          </table>
          {slip.lop_override && slip.adjust_note && <p className="text-[12.5px] text-[#405058] mt-3">Attendance adjusted: {slip.adjust_note}</p>}
          <p className="text-right text-lg mt-4">Net pay: <b>{inr(slip.net)}</b></p>
          <div className="flex justify-between mt-12 text-xs text-[#405058]">
            <div className="border-t border-[#101b22] pt-1 px-6">Employee</div>
            <div className="border-t border-[#101b22] pt-1 px-6">Principal / Accounts</div>
          </div>
        </PrintSheet>
      )}
    </>
  );
}
